import { apiClient } from '@/shared/api';
import type {
  Organisation,
  OrganisationListResponse,
  OrganisationFilters,
} from '../types';

const BASE_PATH = '/v1/admin/organisations';

export async function getOrganisations(
  filters: OrganisationFilters = {}
): Promise<OrganisationListResponse> {
  const params = new URLSearchParams();

  // Only add filters if they have values - no default pagination
  if (filters.search) params.append('search', filters.search);
  if (filters.type) params.append('type', filters.type);
  if (filters.status) params.append('status', filters.status);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const response = await apiClient.get<OrganisationListResponse>(`${BASE_PATH}`, {
    params: params.toString() ? params : undefined
  });
  return response.data;
}

export async function getOrganisation(orgId: string): Promise<Organisation> {
  const response = await apiClient.get<{ data?: { organisation: Organisation }; organisation?: Organisation }>(`${BASE_PATH}/${orgId}`);
  const org = response.data.data?.organisation || response.data.organisation;
  if (!org) throw new Error('Organisation not found in response');
  return org;
}

export async function updateOrganisation(
  orgId: string,
  data: Partial<Organisation>
): Promise<Organisation> {
  // API expects POST with payload wrapped in { organisation: {...} }
  const response = await apiClient.post<{ organisation: Organisation }>(
    `${BASE_PATH}/${orgId}`,
    { organisation: { ...data, organisation_id: orgId } }
  );
  return response.data.organisation;
}

export async function createOrganisation(data: {
  userId: string;
  companyName: string;
  companyType: 'licensee' | 'licensor' | 'retailer' | 'agent';
}): Promise<Organisation | null> {
  const response = await apiClient.post<{
    organisation?: Organisation;
    data?: { organisation?: Organisation } | Record<string, never>;
    status?: string;
  }>(BASE_PATH, data);
  return response.data.organisation || response.data.data?.organisation || null;
}
